import { Tile } from "@carbon/react";
import { useTranslation } from "react-i18next";
import { OfferResponse } from "../../types";
import { currencyFormatter, formatDate } from "../../utils";
import FileReader from "../../components/FileReader/FileReader";
import "../Offer/offer.scss";

/**
 * The seller's own "Offer conditions" panel (Task 6) — the read-only summary of the commercial
 * terms the seller committed to (payment, delivery cost, lead time, validity, comments, file).
 * Carbon-styled `dl`/`dt`/`dd` reusing the `.offer-buyer` grid from `offer.scss`, the same
 * structure `BuyerConditionsPanel` (Task 5) uses for the mirror-image buyer panel. Shared by
 * `SellerOffer.tsx` (single) and `JointSellerOffer.tsx` (joint) — both render one offer's terms.
 */
const SellerOfferConditions = ({ offer }: { offer: OfferResponse }) => {
  const { t } = useTranslation();

  const deliveryCostValue =
    offer?.deliveryPlace === "BUYER"
      ? t("offer.deliveryCostBuyer")
      : offer?.deliveryPlace === "SELLER" && offer?.deliveryCost === 0
        ? t("offer.deliveryIncludedBuyer")
        : offer?.deliveryPlace === "SELLER" && offer?.deliveryCost > 0
          ? currencyFormatter(
              offer?.deliveryCost,
              offer?.deliveryCostCurrency || offer?.currency
            )
          : "—";

  return (
    <Tile className="offer-buyer">
      <p className="offer-buyer__heading">{t("offer.offerConditions")}</p>
      <dl className="offer-buyer__grid">
        <dt>{t("offer.paymentMethod")}</dt>
        <dd>
          {offer?.paymentMethod} {offer?.paymentTerm} {t("analytics.days")}
        </dd>

        <dt>{t("offer.deliveryCost")}</dt>
        <dd>{deliveryCostValue}</dd>

        {offer?.pickupAddress && (
          <>
            <dt>{t("offer.pickupAddress")}</dt>
            <dd>{offer.pickupAddress}</dd>
          </>
        )}

        <dt>{t("offer.leadTimeAfterConfirmation")}</dt>
        <dd>
          {offer?.leadTime} {t("analytics.days")}
        </dd>

        <dt>{t("offer.offerValidUntil")}</dt>
        <dd>{formatDate(offer?.offerValidity ?? "")}</dd>
      </dl>

      {(offer?.generalComments || offer?.fileURL) && (
        <hr className="offer-buyer__divider" />
      )}

      <dl className="offer-buyer__grid">
        {offer?.generalComments && (
          <>
            <dt>{t("offer.buyerComments")}</dt>
            <dd>{offer?.generalComments}</dd>
          </>
        )}
        {offer?.fileURL && (
          <>
            <dt>{t("offer.file")}</dt>
            <dd className="offer-buyer__files">
              <FileReader fileKey={offer?.fileURL} />
            </dd>
          </>
        )}
      </dl>
    </Tile>
  );
};

export default SellerOfferConditions;
